import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Form, FormGroup, Row } from 'reactstrap';
import { change, Field, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import FormItem from '../../../common/forms/FormItem';
import { isRequired, validateNumber } from '../../../../utils/validators';
import Loader from '../../../common/Loader';
import TextInput from '../../../common/forms/input/TextInput';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import CabinCrewSelect from '../../../common/forms/select/CabinCrewSelect';
import FormFooter from '../../../common/forms/FormFooter';
import DriverMovementSelect from '../../../common/forms/select/DriverMovementSelect';
import { MONTH_OPTIONS } from '../../../../config/constants';
import Select from '../../../common/forms/select/Select';

const FORM_NAME = 'CabinCrewMovementHistoryForm';

const isVacationMovement = movement =>
  !!movement && (!!movement.vacation || !!movement.paidVacation);

const toVacationPeriodOption = year => ({ value: year, label: `${year}-${year + 1}` });

const buildVacationPeriodOptions = (fromYear = new Date().getFullYear() - 5) => {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear; y >= fromYear; y -= 1) {
    options.push(toVacationPeriodOption(y));
  }
  return options;
};

const VACATION_PERIOD_OPTIONS = buildVacationPeriodOptions();

const getVacationPeriodOptions = (selectedYear) => {
  if (!selectedYear || VACATION_PERIOD_OPTIONS.some(o => o.value === selectedYear)) {
    return VACATION_PERIOD_OPTIONS;
  }
  return [...VACATION_PERIOD_OPTIONS, toVacationPeriodOption(selectedYear)];
};

const CabinCrewMovementHistoryForm = ({
  handleSubmit,
  loading,
  isEdit,
  dispatchChange,
  initialValues,
  disableSaveButton,
}) => {
  const [hasMaxPerMonth, setHasMaxPerMonth] = useState(false);
  const [hasVacationPeriod, setHasVacationPeriod] = useState(false);
  const [vacationPeriodOptions, setVacationPeriodOptions] = useState(VACATION_PERIOD_OPTIONS);

  const setMonthField = (month) => {
    const monthOption = MONTH_OPTIONS.find(option => option.value === month);
    dispatchChange(FORM_NAME, 'month', {
      value: month,
      label: monthOption ? monthOption.label : '',
    });
  };

  const setVacationPeriodField = (year) => {
    const periodYear = year ? Number(year) : new Date().getFullYear();
    setVacationPeriodOptions(getVacationPeriodOptions(periodYear));
    dispatchChange(FORM_NAME, 'vacationPeriod', toVacationPeriodOption(periodYear));
  };

  useEffect(() => {
    if (initialValues) {
      const {
        cabinCrewMovementMaxDaysPerMonth,
        cabinCrewMovementVacation,
        cabinCrewMovementPaidVacation,
      } = initialValues;

      const maxPerMonth = !!cabinCrewMovementMaxDaysPerMonth;
      const vacation = !!cabinCrewMovementVacation || !!cabinCrewMovementPaidVacation;

      setHasMaxPerMonth(maxPerMonth);
      setHasVacationPeriod(vacation && !maxPerMonth);

      if (maxPerMonth) {
        dispatchChange(FORM_NAME, 'year', initialValues.year);
        setMonthField(initialValues.month ? initialValues.month.value : null);
        dispatchChange(FORM_NAME, 'vacationPeriod', null);
      } else if (vacation) {
        setVacationPeriodField(initialValues.year);
        dispatchChange(FORM_NAME, 'month', null);
        dispatchChange(FORM_NAME, 'year', null);
      } else {
        dispatchChange(FORM_NAME, 'month', null);
        dispatchChange(FORM_NAME, 'year', null);
        dispatchChange(FORM_NAME, 'vacationPeriod', null);
      }
    }
  }, []);

  const handleDriverMovementChange = (value) => {
    const maxPerMonth = !!(value && value.maxDaysPerMonth);
    const vacation = isVacationMovement(value);

    setHasMaxPerMonth(maxPerMonth);
    setHasVacationPeriod(vacation && !maxPerMonth);

    if (maxPerMonth) {
      dispatchChange(FORM_NAME, 'year', new Date().getFullYear());
      setMonthField(new Date().getMonth() + 1);
      dispatchChange(FORM_NAME, 'vacationPeriod', null);
    } else if (vacation) {
      setVacationPeriodField(new Date().getFullYear());
      dispatchChange(FORM_NAME, 'month', null);
      dispatchChange(FORM_NAME, 'year', null);
    } else {
      dispatchChange(FORM_NAME, 'month', null);
      dispatchChange(FORM_NAME, 'year', null);
      dispatchChange(FORM_NAME, 'vacationPeriod', null);
    }
  };

  if (loading) return <Loader />;

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup row>
        <FormItem label="Tripulante" required>
          <Field
            name="cabinCrew"
            component={CabinCrewSelect}
            isDisabled={isEdit}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Motivo" required>
          <Field
            name="driverMovement"
            component={DriverMovementSelect}
            onChange={handleDriverMovementChange}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      {hasMaxPerMonth && (
        <FormGroup row>
          <FormItem label="Mes Asignado" required>
            <Row>
              <Col md={6}>
                <Field
                  name="month"
                  component={Select}
                  options={MONTH_OPTIONS}
                  placeholder="Mes"
                  validate={[isRequired]}
                />
              </Col>
              <Col md={6}>
                <Field
                  name="year"
                  component={TextInput}
                  placeholder="Año"
                  validate={[isRequired, validateNumber]}
                />
              </Col>
            </Row>
          </FormItem>
        </FormGroup>
      )}
      {hasVacationPeriod && (
        <FormGroup row>
          <FormItem label="Periodo vacacional" required>
            <Row>
              <Col md={6}>
                <Field
                  name="vacationPeriod"
                  component={Select}
                  options={vacationPeriodOptions}
                  placeholder="Periodo"
                  validate={[isRequired]}
                />
              </Col>
            </Row>
          </FormItem>
        </FormGroup>
      )}
      <FormGroup row>
        <FormItem label="Fecha/Hora Inicio" required>
          <Field
            name="startDate"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Fecha/Hora Final">
          <Field
            name="endDate"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Observación 1">
          <Field
            name="note"
            component={TextInput}
            type="textarea"
            placeholder="Notas"
            maxLength={255}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Observación 2">
          <Field
            name="secondaryNote"
            component={TextInput}
            type="textarea"
            placeholder="Notas"
            maxLength={255}
          />
        </FormItem>
      </FormGroup>
      <FormFooter saveButtonDisabled={disableSaveButton} />
    </Form>
  );
};

CabinCrewMovementHistoryForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    cabinCrewMovementMaxDaysPerMonth: PropTypes.number,
    cabinCrewMovementVacation: PropTypes.bool,
    cabinCrewMovementPaidVacation: PropTypes.bool,
    month: PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
    year: PropTypes.number,
  }),
  disableSaveButton: PropTypes.bool,
};

CabinCrewMovementHistoryForm.defaultProps = {
  initialValues: null,
  disableSaveButton: false,
};

const mapStateToProps = ({ TrafficUnit }) => ({
  loading: !TrafficUnit.CabinCrew.getIn(['current', 'activity']).isEmpty(),
});

const formComponent = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
})(CabinCrewMovementHistoryForm);

const mapDispatchToProps = {
  dispatchChange: change,
};

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);